import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert,
} from '@mui/material';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadEmailSecurity, setFindingStatus, submitRemediation, approveRemediationRequest } from './emailSlice';
import { severityChipSx, severityLabel, formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const AUTH_COLOR = { pass: tokens.severity.low, fail: tokens.severity.critical, enforced: tokens.severity.low, monitoring: tokens.severity.medium, none: tokens.severity.critical };
const FINDING_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'FALSE_POSITIVE'];
const REMEDIATION_ACTIONS = ['QUARANTINE', 'RELEASE', 'DELETE', 'PURGE_TENANT'];
const PROVIDERS = ['M365', 'GOOGLE_WORKSPACE', 'IMAP_GATEWAY'];
const STATUS_COLOR = {
  PENDING_APPROVAL: tokens.severity.medium, APPROVED: tokens.severity.low, EXECUTING: tokens.severity.medium,
  SUCCEEDED: tokens.severity.low, FAILED: tokens.severity.critical, REJECTED: tokens.severity.critical,
};

export default function EmailPage() {
  const dispatch = useDispatch();
  const { stats, domains, threats, findings, remediations } = useSelector((s) => s.email);
  const [remediateTarget, setRemediateTarget] = useState(null);
  const [remediateForm, setRemediateForm] = useState({ action: 'QUARANTINE', provider: 'M365', reason: '' });
  const [error, setError] = useState(null);

  useEffect(() => { dispatch(loadEmailSecurity()); }, [dispatch]);

  const runAction = async (thunk, arg) => {
    setError(null);
    const result = await dispatch(thunk(arg));
    if (thunk.rejected.match(result)) setError(result.error?.message || 'That action could not be completed.');
  };

  const openRemediate = (threat) => {
    setRemediateTarget(threat);
    setRemediateForm({ action: 'QUARANTINE', provider: 'M365', reason: '' });
  };

  const handleSubmitRemediation = async () => {
    if (remediateForm.reason.trim().length < 10) return setError('Reason must be at least 10 characters — this is logged as the audit justification for the remediation action.');
    await runAction(submitRemediation, { message_id: remediateTarget.id, ...remediateForm });
    setRemediateTarget(null);
  };

  return (
    <Box>
      <PageHeader
        title="Email Security"
        subtitle="Spam filtering, phishing detection, attachment scanning, and domain authentication (SPF/DKIM/DMARC)"
        action={<ModuleControlPanel basePath="/email" moduleLabel="Email Security" />}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {stats && (
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={6} sm={2.4}><StatCard label="Scanned Today" value={stats.scannedToday.toLocaleString()} /></Grid>
          <Grid item xs={6} sm={2.4}><StatCard label="Blocked Today" value={stats.blockedToday} accent={tokens.severity.critical} /></Grid>
          <Grid item xs={6} sm={2.4}><StatCard label="Phishing Caught" value={stats.phishingCaught} accent={tokens.severity.high} /></Grid>
          <Grid item xs={6} sm={2.4}><StatCard label="Malicious Attachments" value={stats.malwareAttachments} accent={tokens.severity.high} /></Grid>
          <Grid item xs={12} sm={2.4}><StatCard label="Spoof Attempts" value={stats.spoofAttempts} accent={tokens.severity.medium} /></Grid>
        </Grid>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ overflow: 'hidden', mb: 2.5 }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Recent Email Threats</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Subject', 'From', 'Type', 'Severity', 'Action', ''].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {threats.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary, maxWidth: 200 }} noWrap>{t.subject}</TableCell>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', color: tokens.text.secondary }}>{t.from}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{t.type}</TableCell>
                    <TableCell><Chip label={severityLabel(t.severity)} size="small" sx={severityChipSx(t.severity)} /></TableCell>
                    <TableCell>
                      <Chip label={t.action} size="small" sx={{ color: tokens.severity.low, border: `1px solid ${tokens.severity.low}`, backgroundColor: 'transparent' }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Request mailbox remediation (quarantine / release / delete)">
                        <IconButton size="small" onClick={() => openRemediate(t)}><ShieldRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!threats.length && <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>No threats detected.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Findings</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Title', 'Category', 'Severity', 'Confidence', 'Status'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {findings.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary, maxWidth: 220 }} noWrap>{f.title}</TableCell>
                    <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.secondary }}>{f.category}</TableCell>
                    <TableCell><Chip label={severityLabel(String(f.severity).toLowerCase())} size="small" sx={severityChipSx(String(f.severity).toLowerCase())} /></TableCell>
                    <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.secondary }}>{f.confidence}%</TableCell>
                    <TableCell>
                      <TextField select size="small" variant="standard" value={f.status}
                        onChange={(e) => runAction(setFindingStatus, { id: f.id, status: e.target.value })} sx={{ minWidth: 130 }}>
                        {FINDING_STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replaceAll('_', ' ')}</MenuItem>)}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
                {!findings.length && <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>No findings recorded yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Domain Authentication</Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  {['Domain', 'SPF', 'DKIM', 'DMARC'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.65rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.map((d) => (
                  <TableRow key={d.domain}>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: tokens.text.primary }}>{d.domain}</TableCell>
                    <TableCell><Chip label={d.spf} size="small" sx={{ color: AUTH_COLOR[d.spf], border: `1px solid ${AUTH_COLOR[d.spf]}`, backgroundColor: 'transparent', textTransform: 'uppercase' }} /></TableCell>
                    <TableCell><Chip label={d.dkim} size="small" sx={{ color: AUTH_COLOR[d.dkim], border: `1px solid ${AUTH_COLOR[d.dkim]}`, backgroundColor: 'transparent', textTransform: 'uppercase' }} /></TableCell>
                    <TableCell><Chip label={d.dmarc} size="small" sx={{ color: AUTH_COLOR[d.dmarc], border: `1px solid ${AUTH_COLOR[d.dmarc]}`, backgroundColor: 'transparent', textTransform: 'uppercase' }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Mailbox Remediations</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted, mb: 1.5 }}>Approval is required before a remediation executes — the requester cannot approve their own request.</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Action', 'Provider', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.65rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {remediations.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.primary }}>{r.action}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.secondary }}>{r.provider}</TableCell>
                    <TableCell><Chip label={r.status.replaceAll('_', ' ')} size="small" sx={{ color: STATUS_COLOR[r.status], border: `1px solid ${STATUS_COLOR[r.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                    <TableCell>
                      {r.status === 'PENDING_APPROVAL' && (
                        <Button size="small" onClick={() => runAction(approveRemediationRequest, r.id)}>Approve</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!remediations.length && <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.74rem', py: 2 }}>No remediations requested yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={Boolean(remediateTarget)} onClose={() => setRemediateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Request Mailbox Remediation</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.78rem', color: tokens.text.secondary, mb: 2 }}>{remediateTarget?.subject}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField select label="Action" size="small" value={remediateForm.action}
              onChange={(e) => setRemediateForm((f) => ({ ...f, action: e.target.value }))}>
              {REMEDIATION_ACTIONS.map((a) => <MenuItem key={a} value={a}>{a.replaceAll('_', ' ')}</MenuItem>)}
            </TextField>
            <TextField select label="Mailbox Provider" size="small" value={remediateForm.provider}
              onChange={(e) => setRemediateForm((f) => ({ ...f, provider: e.target.value }))}>
              {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p.replaceAll('_', ' ')}</MenuItem>)}
            </TextField>
            <TextField label="Reason" size="small" multiline minRows={2} value={remediateForm.reason}
              onChange={(e) => setRemediateForm((f) => ({ ...f, reason: e.target.value }))}
              helperText="Required — logged as the audit justification (min 10 characters)." />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemediateTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitRemediation}>Submit for Approval</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
